import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { apiFetch } from '../utils/apiFetch'

export interface PollOption {
  _id: string
  text: string
}

export interface PollResult {
  optionId: string
  count: number
  total: number
}

export interface Poll {
  _id: string
  title: string
  description?: string
  options: PollOption[]
  createdAt: string
  closedAt?: string
  createdByMe: boolean
  userResponse: { optionId?: string; dismissed: boolean } | null
  results: PollResult[] | null
}

interface PollContextValue {
  polls: Poll[]
  pendingCount: number
  loading: boolean
  refresh: () => void
  updatePoll: (poll: Poll) => void
  prependPoll: (poll: Poll) => void
}

const PollContext = createContext<PollContextValue>({
  polls: [],
  pendingCount: 0,
  loading: true,
  refresh: () => {},
  updatePoll: () => {},
  prependPoll: () => {},
})

export function PollProvider({ children }: { children: React.ReactNode }) {
  const [polls, setPolls] = useState<Poll[]>([])
  const [loading, setLoading] = useState(true)

  const pendingCount = polls.filter(poll => !poll.userResponse && !poll.closedAt).length

  const refresh = useCallback(() => {
    apiFetch('/api/polls')
      .then(response => response.json())
      .then((data: Poll[]) => {
        setPolls(data)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [])

  const updatePoll = useCallback((updatedPoll: Poll) => {
    setPolls(prev => prev.map(existing => existing._id === updatedPoll._id ? updatedPoll : existing))
  }, [])

  const prependPoll = useCallback((newPoll: Poll) => {
    setPolls(prev => [newPoll, ...prev])
  }, [])

  useEffect(() => { refresh() }, [refresh])

  return (
    <PollContext.Provider value={{ polls, pendingCount, loading, refresh, updatePoll, prependPoll }}>
      {children}
    </PollContext.Provider>
  )
}

export const usePollContext = () => useContext(PollContext)
